import React, { useEffect, useMemo, useState } from 'react';
import PdaPage from '../components/PdaPage';
import {
  listMyCompanies,
  PdaCompany,
  switchOrganization,
} from '../services/api';
import { navigateTo } from '../utils/navigation';
import { getPdaErrorMessage } from '../utils/pdaErrors';
import { notifyError, notifyInfo, notifySuccess } from '../utils/pdaFeedback';
import {
  clearSession,
  getStoredUser,
  storeCompany,
  storeSession,
} from '../utils/session';

function getCompanyDisplayName(company: PdaCompany) {
  return company.name || company.code || `公司 ${company.id}`;
}

function CompanySelect() {
  const user = getStoredUser();
  const displayName = user?.full_name || user?.username || user?.emp_no || '员工';
  const [companies, setCompanies] = useState<PdaCompany[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [switching, setSwitching] = useState(false);
  const [errorText, setErrorText] = useState('');

  const selectedCompany = useMemo(
    () => companies.find((company) => company.id === selectedCompanyId) || null,
    [companies, selectedCompanyId],
  );

  useEffect(() => {
    void loadCompanies();
  }, []);

  async function loadCompanies() {
    setLoading(true);
    setErrorText('');

    try {
      const response = await listMyCompanies();
      const enabledCompanies = (response.data?.companies || []).filter(
        (company) => company.status == null || company.status === 1,
      );
      const currentCompany = enabledCompanies.find(
        (company) => company.id === response.data?.current_company_id,
      );

      setCompanies(enabledCompanies);
      setSelectedCompanyId((currentCompany || enabledCompanies[0])?.id ?? null);
    } catch (error) {
      setErrorText(getPdaErrorMessage(error, '公司列表读取失败'));
    } finally {
      setLoading(false);
    }
  }

  function handleRescan() {
    clearSession();
    notifyInfo('请重新扫员工卡');
    navigateTo('/login', true);
  }

  async function handleEnter() {
    if (!selectedCompany || switching) {
      return;
    }

    setSwitching(true);
    try {
      const response = await switchOrganization(selectedCompany.id);
      const data = response.data;
      const nextCompany = data.company || selectedCompany;

      if (!data.token || !user) {
        throw new Error('切换公司返回数据不完整');
      }

      storeSession({
        ...user,
        company_code: nextCompany.code,
        company_id: nextCompany.id,
        company_name: nextCompany.name,
        role_code: data.role_code || nextCompany.role_code,
        role_name: data.role_name || nextCompany.role_name,
        token: data.token,
      });
      storeCompany({
        ...nextCompany,
        role_code: data.role_code || nextCompany.role_code,
        role_name: data.role_name || nextCompany.role_name,
      });

      notifySuccess(`已进入 ${getCompanyDisplayName(nextCompany)}`);
      navigateTo('/home', true);
    } catch (error) {
      notifyError(getPdaErrorMessage(error, '切换公司失败'));
    } finally {
      setSwitching(false);
    }
  }

  return (
    <PdaPage
      actionLabel="重新扫码"
      onAction={handleRescan}
      pageClassName="company-select-page"
      subtitle={user?.emp_no ? `${displayName} · ${user.emp_no}` : displayName}
      title="选择公司"
    >
      <section className="company-select-intro">
        <div className="company-select-intro__title">请选择本次作业公司</div>
        <div className="company-select-intro__hint">选择后进入扫码主页。</div>
      </section>

      {loading ? (
        <section className="company-select-state">正在读取可用公司...</section>
      ) : null}

      {!loading && errorText ? (
        <section className="company-select-state company-select-state--error">
          <div>{errorText}</div>
          <button className="secondary-btn" onClick={() => void loadCompanies()} type="button">
            重新读取
          </button>
        </section>
      ) : null}

      {!loading && !errorText && companies.length === 0 ? (
        <section className="company-select-state">
          <div>当前账号没有可用公司。</div>
          <button className="secondary-btn" onClick={handleRescan} type="button">
            重新扫码
          </button>
        </section>
      ) : null}

      {!loading && !errorText && companies.length > 0 ? (
        <section className="company-list" aria-label="公司列表">
          {companies.map((company) => {
            const selected = company.id === selectedCompanyId;

            return (
              <button
                aria-pressed={selected}
                className={selected ? 'company-option company-option--selected' : 'company-option'}
                key={company.id}
                onClick={() => setSelectedCompanyId(company.id)}
                type="button"
              >
                <span className="company-option__code">{company.code || '公司'}</span>
                <span className="company-option__main">
                  <strong>{getCompanyDisplayName(company)}</strong>
                  <small>{company.role_name || '可用'}</small>
                </span>
                <span className={selected ? 'company-option__status company-option__status--selected' : 'company-option__status'}>
                  {selected ? '已选' : '选择'}
                </span>
              </button>
            );
          })}
        </section>
      ) : null}

      <div className="company-select-actions">
        <button
          className="primary-btn"
          disabled={!selectedCompany || loading || switching}
          onClick={() => void handleEnter()}
          type="button"
        >
          {switching ? '进入中...' : '进入系统'}
        </button>
      </div>
    </PdaPage>
  );
}

export default CompanySelect;
